import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { API_URL } from "../config/site";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
      } catch {
        // keep polling
      }
      attempts += 1;
      if (attempts < 12) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="py-24">
      <div className="max-w-lg mx-auto px-6 text-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
          {status === "checking" && (
            <>
              <Loader2 className="w-14 h-14 text-lilac animate-spin mx-auto" />
              <h1 className="mt-6 font-serif text-3xl text-plum">Stiamo confermando il tuo ordine...</h1>
              <p className="mt-3 text-sm text-plum/50">Qualche secondo e ci siamo.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-14 h-14 text-lilac-dark mx-auto" data-testid="payment-confirmed-icon" />
              <h1 className="mt-6 font-serif text-3xl sm:text-4xl text-plum">Grazie, il tuo ordine è confermato</h1>
              <p className="mt-4 text-base text-plum/60 leading-relaxed">
                Il tuo percorso di cura sta per iniziare. Riceverai presto tutti i dettagli via email.
              </p>
              <Link
                to="/shop"
                data-testid="success-back-to-shop"
                className="inline-block mt-8 px-8 py-4 rounded-full bg-lilac text-white font-bold text-sm uppercase tracking-[0.14em] hover:bg-lilac-dark transition-colors"
              >
                Torna allo shop
              </Link>
            </>
          )}
          {(status === "error" || status === "timeout") && (
            <>
              <XCircle className="w-14 h-14 text-plum/50 mx-auto" />
              <h1 className="mt-6 font-serif text-3xl text-plum">Non riusciamo a confermare il pagamento</h1>
              <p className="mt-4 text-base text-plum/60">
                Se hai completato il pagamento, l'ordine verrà comunque registrato a breve. Per qualsiasi dubbio scrivici.
              </p>
              <Link to="/contatti" data-testid="success-contact-link" className="inline-block mt-8 px-8 py-4 rounded-full border-2 border-plum/15 text-plum font-bold text-sm uppercase tracking-[0.14em] hover:border-lilac-dark hover:text-lilac-dark transition-colors">
                Contattaci
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </main>
  );
}

export function PaymentCancel() {
  return (
    <main data-testid="payment-cancel-page" className="py-24">
      <div className="max-w-lg mx-auto px-6 text-center">
        <XCircle className="w-14 h-14 text-plum/30 mx-auto" />
        <h1 className="mt-6 font-serif text-3xl sm:text-4xl text-plum">Pagamento annullato</h1>
        <p className="mt-4 text-base text-plum/60">
          Nessun addebito è stato effettuato. Il tuo carrello è ancora qui, pronto quando vuoi tu.
        </p>
        <Link
          to="/shop"
          data-testid="cancel-back-to-shop"
          className="inline-block mt-8 px-8 py-4 rounded-full bg-lilac text-white font-bold text-sm uppercase tracking-[0.14em] hover:bg-lilac-dark transition-colors"
        >
          Torna allo shop
        </Link>
      </div>
    </main>
  );
}
